/**
 * Gallery Modal System
 * Extends the existing Alpine.js modal system to display gallery images in an overlay
 */

// Wait for Alpine to be fully loaded
document.addEventListener('alpine:init', () => {
  // Register the gallery modal store
  Alpine.store('galleryModal', {
    images: [],
    currentIndex: 0,
    loading: false,
    
    get currentImage() {
      return this.images[this.currentIndex] || null;
    },

    getSrcset(imageUrl) {
      // Generate srcset with multiple sizes for responsive loading
      if (!imageUrl || !imageUrl.includes('cdn.shopify.com')) {
        return '';
      }

      const baseUrl = imageUrl.split('?')[0];
      // Generate common responsive sizes
      const sizes = [640, 960, 1280, 1920, 2560];

      return sizes
        .map(width => `${baseUrl}?v=now&width=${width} ${width}w`)
        .join(', ');
    },

    open(galleryId, imageIndex = 0) {
      // Extract images from the gallery
      this.extractImages(galleryId);
      this.currentIndex = Math.max(0, Math.min(imageIndex, this.images.length - 1));
      this.loading = true;

      // Open the modal using the existing system
      Alpine.store('modals').open('gallery-modal');

      // Preload adjacent images for faster navigation
      this.preloadAdjacentImages();
    },

    close() {
      Alpine.store('modals').close('gallery-modal');
      this.images = [];
      this.currentIndex = 0;
      this.loading = false;
    },

    next() {
      if (this.images.length > 1) {
        this.loading = true;
        this.currentIndex = (this.currentIndex + 1) % this.images.length;
        // Preload adjacent images for smooth navigation
        this.preloadAdjacentImages();
      }
    },

    previous() {
      if (this.images.length > 1) {
        this.loading = true;
        this.currentIndex = this.currentIndex === 0 ? this.images.length - 1 : this.currentIndex - 1;
        // Preload adjacent images for smooth navigation
        this.preloadAdjacentImages();
      }
    },

    extractImages(galleryId) {
      const gallery = document.querySelector(`#${galleryId}, [data-gallery-id="${galleryId}"]`);
      if (!gallery) {
        console.warn(`Gallery not found: ${galleryId}`);
        return;
      }

      const imageElements = gallery.querySelectorAll('img[src], .tile-image');
      this.images = Array.from(imageElements).map((element, index) => {
        return this.extractImageData(element, index);
      }).filter(Boolean);
    },

    extractImageData(element, index) {
      let imageData = {
        url: '',
        alt: '',
        caption: '',
        index: index
      };

      // Handle different image formats
      if (element.tagName === 'IMG') {
        // Direct img element
        imageData.url = this.getHighResUrl(element.src);
        imageData.alt = element.alt || '';
        
        // Look for caption in nearby elements
        const tile = element.closest('li, .tile, .grid-item');
        if (tile) {
          const captionEl = tile.querySelector('.tile-content-wrapper, .caption, [data-caption]');
          if (captionEl) {
            imageData.caption = captionEl.textContent.trim();
          }
        }
      } else {
        // Handle other formats - look for img within the element
        const img = element.querySelector('img');
        if (img) {
          imageData.url = this.getHighResUrl(img.src);
          imageData.alt = img.alt || '';
        }
      }

      return imageData.url ? imageData : null;
    },

    getOptimalWidth() {
      // Calculate optimal width based on viewport and pixel density
      const viewportWidth = window.innerWidth;
      const devicePixelRatio = window.devicePixelRatio || 1;
      const optimalWidth = Math.ceil(viewportWidth * devicePixelRatio);

      // Round to nearest Shopify CDN size for better caching
      // Shopify supports: 180, 360, 540, 720, 900, 1080, 1296, 1512, 1728, 2048
      const sizes = [180, 360, 540, 720, 900, 1080, 1296, 1512, 1728, 2048];
      return sizes.find(s => s >= optimalWidth) || 2048;
    },

    getHighResUrl(originalUrl, width = null) {
      // Convert to high-resolution URL for modal display
      if (!originalUrl) return '';

      // For Shopify CDN URLs, request optimized resolution
      if (originalUrl.includes('cdn.shopify.com')) {
        // Remove existing size parameters and add high-res parameter
        const baseUrl = originalUrl.split('?')[0];
        const optimalWidth = width || this.getOptimalWidth();
        return `${baseUrl}?v=now&width=${optimalWidth}`;
      }

      return originalUrl;
    },

    preloadAdjacentImages() {
      // Preload current, next, and previous images for instant navigation
      if (this.images.length === 0) return;

      const indicesToPreload = new Set();

      // Always preload current
      indicesToPreload.add(this.currentIndex);

      // Preload next image
      if (this.images.length > 1) {
        indicesToPreload.add((this.currentIndex + 1) % this.images.length);
      }

      // Preload previous image
      if (this.images.length > 1) {
        const prevIndex = this.currentIndex === 0 ? this.images.length - 1 : this.currentIndex - 1;
        indicesToPreload.add(prevIndex);
      }

      // Preload next-next image for smoother fast navigation
      if (this.images.length > 2) {
        indicesToPreload.add((this.currentIndex + 2) % this.images.length);
      }

      // Create Image objects to trigger browser preload
      indicesToPreload.forEach(index => {
        if (this.images[index]) {
          const img = new Image();
          img.src = this.images[index].url;
        }
      });
    }
  });
});

// Wait for both Alpine and modals to be ready before registering
document.addEventListener('DOMContentLoaded', () => {
  // Wait a bit for all Alpine stores to be initialized
  setTimeout(() => {
    if (window.Alpine && Alpine.store('modals')) {
      // Register the modal with the existing system
      Alpine.store('modals').register('gallery-modal', 'modal');
    } else {
      console.warn('Modals store not available, retrying...');
      // Retry after a longer delay
      setTimeout(() => {
        if (window.Alpine && Alpine.store('modals')) {
          Alpine.store('modals').register('gallery-modal', 'modal');
        }
      }, 1000);
    }
  }, 100);
});

// Keyboard controls
document.addEventListener('keydown', (e) => {
  const galleryModal = Alpine.store('galleryModal');
  const modalsStore = Alpine.store('modals');
  
  if (modalsStore.modal.contents === 'gallery-modal' && modalsStore.modal.open) {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        galleryModal.previous();
        break;
      case 'ArrowRight':
        e.preventDefault();
        galleryModal.next();
        break;
      case 'Escape':
        e.preventDefault();
        galleryModal.close();
        break;
    }
  }
});

// Gallery Modal Click Handler
class GalleryModalHandler {
  constructor() {
    this.init();
  }

  init() {
    // Auto-initialize for gallery containers
    this.setupGalleryClickHandlers();
    
    // Re-initialize when content changes
    document.addEventListener('DOMContentLoaded', () => {
      this.setupGalleryClickHandlers();
    });
  }

  setupGalleryClickHandlers() {
    // Find all galleries: image galleries, masonry galleries, and custom galleries
    const galleries = document.querySelectorAll(
      '[data-random-spacing="true"], [id^="gallery-"], [id^="masonry-gallery-"], .image-gallery-section ul, .ssg-items-wrapper'
    );
    
    galleries.forEach((gallery, galleryIndex) => {
      if (gallery.dataset.galleryModalInit) return; // Already initialized
      
      // Generate or get gallery ID
      let galleryId = gallery.id;
      if (!galleryId) {
        galleryId = `gallery-${galleryIndex}`;
        gallery.id = galleryId;
      }
      
      // Add click handlers to images in this gallery
      // Handle both complex gallery structures and simple masonry structures
      // Exclude masonry structural elements (grid-sizer, gutter-sizer)
      const imageContainers = gallery.querySelectorAll('li:not(.grid-sizer):not(.gutter-sizer), .tile, .grid-item, .masonry-gallery-item');
      imageContainers.forEach((container, imageIndex) => {
        const img = container.querySelector('img');
        let link = container.querySelector('a.tile-link');
        
        // If no existing link, create one for the image
        if (img && !link) {
          link = document.createElement('a');
          link.href = '#';
          link.className = 'tile-link';
          link.style.position = 'absolute';
          link.style.inset = '0';
          link.style.zIndex = '10';
          container.style.position = 'relative';
          container.appendChild(link);
        }
        
        if (img && link) {
          // Override the existing link behavior for gallery modal
          link.addEventListener('click', (e) => {
            e.preventDefault();
            Alpine.store('galleryModal').open(galleryId, imageIndex);
          });

          // Add hover prefetching for instant modal loads
          let prefetched = false;
          link.addEventListener('mouseenter', () => {
            if (!prefetched) {
              const galleryModal = Alpine.store('galleryModal');
              const highResUrl = galleryModal.getHighResUrl(img.src);

              // Prefetch the full-res image
              const prefetchImg = new Image();
              prefetchImg.src = highResUrl;

              prefetched = true;
            }
          }, { once: false });

          // Add cursor pointer
          link.style.cursor = 'zoom-in';
        }
      });
      
      gallery.dataset.galleryModalInit = 'true';
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new GalleryModalHandler();
  });
} else {
  new GalleryModalHandler();
}

// Export for manual initialization
window.GalleryModalHandler = GalleryModalHandler;